import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import type {
  Tarefa,
  TarefaInput,
  TarefaPrioridade,
  TarefaStatus,
} from "@/types/tarefas";

// Tabelas ainda não estão nos tipos gerados do Supabase.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = () => supabase as any;

const TAREFA_SELECT = `
  *,
  responsavel:profiles!responsavel_id(id, nome, email),
  criador:profiles!criado_por(id, nome, email),
  cliente:clientes(id, nome, telefone),
  participantes:tarefa_participantes(
    tarefa_id,
    user_id,
    created_at,
    profile:profiles!user_id(id, nome, email)
  )
`;

export interface TarefasFiltro {
  busca?: string | null;
  status?: TarefaStatus | null;
  prioridade?: TarefaPrioridade | null;
  responsavelId?: string | null;
  apenasMinhas?: boolean;
  comCliente?: boolean | null; // true = só com cliente, false = só internas, null = todas
  prazoDe?: string | null;
  prazoAte?: string | null;
  /** Intervalo de calendário (início/fim ISO do mês/semana) */
  prazoRange?: { de: string; ate: string } | null;
}

function mapTarefa(row: Record<string, unknown>): Tarefa {
  return row as unknown as Tarefa;
}

export function useTarefas(filtro?: TarefasFiltro) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["tarefas", filtro ?? null, filtro?.apenasMinhas ? user?.id : null],
    queryFn: async (): Promise<Tarefa[]> => {
      let q = db()
        .from("tarefas")
        .select(TAREFA_SELECT)
        .order("prazo", { ascending: true, nullsFirst: false })
        .order("created_at", { ascending: false });

      if (filtro?.status) q = q.eq("status", filtro.status);
      if (filtro?.prioridade) q = q.eq("prioridade", filtro.prioridade);
      if (filtro?.responsavelId) q = q.eq("responsavel_id", filtro.responsavelId);
      if (filtro?.apenasMinhas && user?.id) {
        // Responsável OU participante — participantes filtrados no client após fetch
        // PostgREST: or on FK is awkward; fetch by responsavel/criado and filter participantes client-side
        q = q.or(`responsavel_id.eq.${user.id},criado_por.eq.${user.id}`);
      }
      if (filtro?.comCliente === true) q = q.not("cliente_id", "is", null);
      if (filtro?.comCliente === false) q = q.is("cliente_id", null);
      if (filtro?.prazoDe) q = q.gte("prazo", filtro.prazoDe);
      if (filtro?.prazoAte) q = q.lte("prazo", filtro.prazoAte);
      if (filtro?.prazoRange) {
        q = q.gte("prazo", filtro.prazoRange.de).lte("prazo", filtro.prazoRange.ate);
      }
      if (filtro?.busca?.trim()) {
        const term = `%${filtro.busca.trim()}%`;
        q = q.ilike("titulo", term);
      }

      const { data, error } = await q;
      if (error) throw error;

      let rows = ((data ?? []) as Record<string, unknown>[]).map(mapTarefa);

      // Inclui tarefas onde o user é só participante (quando "apenasMinhas")
      if (filtro?.apenasMinhas && user?.id) {
        const { data: partRows, error: partErr } = await db()
          .from("tarefa_participantes")
          .select("tarefa_id")
          .eq("user_id", user.id);
        if (partErr) throw partErr;
        const missingIds = ((partRows ?? []) as { tarefa_id: string }[])
          .map((r) => r.tarefa_id)
          .filter((id) => !rows.some((r) => r.id === id));
        if (missingIds.length > 0) {
          const { data: extras, error: exErr } = await db()
            .from("tarefas")
            .select(TAREFA_SELECT)
            .in("id", missingIds);
          if (exErr) throw exErr;
          rows = rows.concat(
            ((extras ?? []) as Record<string, unknown>[]).map(mapTarefa)
          );
        }
      }

      return rows;
    },
    staleTime: 30 * 1000,
  });
}

export function useTarefa(id: string | null) {
  return useQuery({
    queryKey: ["tarefas", "detail", id],
    enabled: !!id,
    queryFn: async (): Promise<Tarefa | null> => {
      const { data, error } = await db()
        .from("tarefas")
        .select(TAREFA_SELECT)
        .eq("id", id)
        .maybeSingle();
      if (error) throw error;
      return data ? mapTarefa(data as Record<string, unknown>) : null;
    },
  });
}

export function usePerfisAtivosTarefas() {
  return useQuery({
    queryKey: ["tarefas", "perfis-ativos"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, nome, email, ativo")
        .eq("ativo", true)
        .order("nome");
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

async function syncParticipantes(tarefaId: string, participanteIds: string[], responsavelId: string) {
  const ids = [...new Set(participanteIds.filter((id) => id && id !== responsavelId))];
  const { error: delErr } = await db()
    .from("tarefa_participantes")
    .delete()
    .eq("tarefa_id", tarefaId);
  if (delErr) throw delErr;
  if (ids.length === 0) return;
  const { error: insErr } = await db()
    .from("tarefa_participantes")
    .insert(ids.map((user_id) => ({ tarefa_id: tarefaId, user_id })));
  if (insErr) throw insErr;
}

export function useCriarTarefa() {
  const qc = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: TarefaInput) => {
      if (!user?.id) throw new Error("Usuário não autenticado");
      const payload = {
        titulo: input.titulo.trim(),
        descricao: input.descricao?.trim() || null,
        status: input.status ?? "a_fazer",
        prioridade: input.prioridade ?? "media",
        prazo: input.prazo || null,
        responsavel_id: input.responsavel_id,
        criado_por: user.id,
        cliente_id: input.cliente_id ?? null,
        concluida_em: input.status === "concluida" ? new Date().toISOString() : null,
      };
      const { data, error } = await db()
        .from("tarefas")
        .insert(payload)
        .select("id")
        .single();
      if (error) throw error;
      await syncParticipantes(data.id, input.participante_ids ?? [], input.responsavel_id);
      return data.id as string;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tarefas"] });
      toast.success("Tarefa criada");
    },
    onError: (err: Error) => {
      console.error(err);
      toast.error(err.message || "Erro ao criar tarefa");
    },
  });
}

export function useAtualizarTarefa() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...input
    }: TarefaInput & { id: string }) => {
      const payload: Record<string, unknown> = {
        titulo: input.titulo.trim(),
        descricao: input.descricao?.trim() || null,
        prioridade: input.prioridade ?? "media",
        prazo: input.prazo || null,
        responsavel_id: input.responsavel_id,
        cliente_id: input.cliente_id ?? null,
      };
      if (input.status) payload.status = input.status;

      const { error } = await db().from("tarefas").update(payload).eq("id", id);
      if (error) throw error;
      if (input.participante_ids !== undefined) {
        await syncParticipantes(id, input.participante_ids, input.responsavel_id);
      }
      return id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tarefas"] });
      toast.success("Tarefa atualizada");
    },
    onError: (err: Error) => {
      console.error(err);
      toast.error(err.message || "Erro ao atualizar tarefa");
    },
  });
}

export function useAtualizarStatusTarefa() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: TarefaStatus }) => {
      const { error } = await db().from("tarefas").update({ status }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tarefas"] });
    },
    onError: (err: Error) => {
      console.error(err);
      toast.error(err.message || "Erro ao mover tarefa");
    },
  });
}

export function useExcluirTarefa() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await db().from("tarefas").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tarefas"] });
      toast.success("Tarefa excluída");
    },
    onError: (err: Error) => {
      console.error(err);
      toast.error(err.message || "Erro ao excluir tarefa");
    },
  });
}

/** Contagem de tarefas abertas atrasadas + com prazo hoje (para badge do menu). */
export function useTarefasBadgeCount() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["tarefas", "badge", user?.id],
    enabled: !!user?.id,
    queryFn: async (): Promise<number> => {
      const agora = new Date();
      const inicioHoje = new Date(agora);
      inicioHoje.setHours(0, 0, 0, 0);
      const fimHoje = new Date(agora);
      fimHoje.setHours(23, 59, 59, 999);

      const { data, error } = await db()
        .from("tarefas")
        .select("id, prazo, status, responsavel_id, criado_por, participantes:tarefa_participantes(user_id)")
        .in("status", ["a_fazer", "em_andamento", "aguardando"])
        .not("prazo", "is", null)
        .lte("prazo", fimHoje.toISOString());

      // Tabela ainda não migrada / sem permissão: badge fica 0
      if (error) {
        console.warn("[tarefas badge]", error.message);
        return 0;
      }

      const uid = user!.id;
      const rows = (data ?? []) as {
        id: string;
        prazo: string;
        responsavel_id: string;
        criado_por: string;
        participantes?: { user_id: string }[];
      }[];

      // Badge só conta "minhas" envolvidas (útil para todos os papéis)
      return rows.filter((t) => {
        const envolvido =
          t.responsavel_id === uid ||
          t.criado_por === uid ||
          (t.participantes ?? []).some((p) => p.user_id === uid);
        return envolvido && t.prazo && new Date(t.prazo) <= fimHoje;
      }).length;
    },
    staleTime: 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
  });
}
